import { useFetch, useEdit, useCreate } from '../react-query';
import { apiClient } from '../../apiHelper';
import { AxiosResponse } from 'axios';
import { pregnancyQueryKeys } from './pregnancy-query-keys';

// TypeScript interfaces for pregnancy data
export interface PregnancyData {
  id?: number;
  patient_id: string | number;
  last_menstrual_period?: string;
  dating_method?: string;
  pregnancy_status?: string;
  estimated_due_date?: string;
  ultrasound_date?: string;
  closed_at?: string | null;
  outcome_date?: string | null;
  current_gestational_age_weeks?: number;
  current_gestational_age_days?: number;
  is_medically_active?: boolean;
  study_date?: string;
  grossesse_classee_a_risque?: boolean | null;
  risque_anemie?: boolean;
  risque_hta?: boolean;
  risque_diabete?: boolean;
  risque_cardiopathie?: boolean;
  risque_metrorragie?: boolean;
  risque_infection?: boolean;
  risque_preeclampsie?: boolean;
  risque_eclampsie?: boolean;
  risque_autre_details?: string | null;
  [key: string]: unknown;
}

export interface PregnancyResponse {
  pregnancy: PregnancyData | null;
  available_actions: string[];
  recommendations: {
    can_create_new: boolean;
    can_update_existing: boolean;
    is_medically_active?: boolean;
    message: string;
  };
}

export function useCreatePregnancy(
  onSuccess?: (response: AxiosResponse, data: PregnancyData) => void,
  onError?: (response: AxiosResponse, error: Error) => void
) {
  const createPregnancy = async (data: PregnancyData) => {
    const response = await apiClient.post('/pregnancy/create', data);
    return response;
  };

  return useCreate(
    createPregnancy,
    pregnancyQueryKeys.all,
    'Failed to create pregnancy',
    'Pregnancy created successfully',
    onSuccess,
    onError
  );
}

export function useUpdatePregnancy(
  onSuccess?: (response: AxiosResponse, data: PregnancyData) => void,
  onError?: (response: AxiosResponse, error: Error) => void
) {
  const updatePregnancy = async (data: PregnancyData & { id: number }) => {
    const { id, ...updatePayload } = data;
    const response = await apiClient.put(
      `/pregnancy/update/${id}`,
      updatePayload
    );
    return response.data;
  };

  return useEdit(
    updatePregnancy,
    pregnancyQueryKeys.all,
    'Failed to update pregnancy',
    'Pregnancy updated successfully',
    onSuccess,
    onError
  );
}

export function useCreateOrUpdatePregnancy(
  onSuccess?: (response: AxiosResponse, data: PregnancyData) => void,
  onError?: (response: AxiosResponse, error: Error) => void
) {
  const createOrUpdatePregnancy = async (data: PregnancyData) => {
    const response = await apiClient.post(
      `/pregnancy/create-or-update/${data.patient_id}`,
      data
    );
    return response;
  };

  return useCreate(
    createOrUpdatePregnancy,
    pregnancyQueryKeys.all,
    'Failed to save pregnancy',
    'Pregnancy saved successfully',
    onSuccess,
    onError
  );
}

export function useGetLatestPregnancy(patient_id: string) {
  return useFetch<PregnancyResponse>(
    async () => {
      const response = await apiClient.get(`/pregnancy/latest/${patient_id}`);
      return response.data; // Return the data directly since it's already in the expected format
    },
    ['pregnancy', 'latest_pregnancy', patient_id],
    {
      enabled: !!patient_id,
    }
  );
}

export function useGetPregnancyById(pregnancyId: number | null | undefined) {
  return useFetch(
    async () => {
      const response = await apiClient.get(`/pregnancy/${pregnancyId}`);
      return response.data;
    },
    pregnancyQueryKeys.detail(pregnancyId ?? 'missing'),
    {
      enabled: Boolean(pregnancyId),
    }
  );
}
